import { createContext, useContext, useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { devWarn } from "@/lib/logger";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking, false = anonymous, {} = user
  const [error, setError] = useState("");

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const { data } = await api.get("/auth/me", { validateStatus: (s) => s < 500 });
        if (!mounted) return;
        if (data && data.email) setUser(data);
        else setUser(false);
      } catch (e) {
        devWarn("[auth/me] failed", e?.message);
        if (mounted) setUser(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, []);

  const login = async (email, password) => {
    setError("");
    try {
      const { data } = await api.post("/auth/login", { email, password });
      if (data.token) {
        try { localStorage.setItem("nhp_token", data.token); } catch { /* storage blocked — cookie auth still applies */ }
      }
      setUser(data);
      return true;
    } catch (e) {
      const msg = formatApiErrorDetail(e.response?.data?.detail) || e.message;
      setError(msg);
      return false;
    }
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch (e) { devWarn("[auth/logout] server error, clearing local state anyway", e?.message); }
    try { localStorage.removeItem("nhp_token"); } catch { /* storage blocked */ }
    setUser(false);
  };

  return (
    <AuthContext.Provider value={{ user, login, logout, error, setError }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
